import { CheckIcon, ChevronIcon, CrossIcon, SearchIcon } from 'assets/icons';
import { Spinner } from 'common/spinner';
import { memoriesOf } from 'helpers/memories';
import { classnames, excludeDefaultAlbum, sleep } from 'helpers/utils';
import { useMediaQuery } from 'hooks/use-media-query';
import { useHorizontalScroll } from 'hooks/use-horizontal-scroll';
import { Album } from 'models/album';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { AlbumContentSerialized } from 'networking/types/album-detail';
import { useCreateAlbum } from 'pages/vault-detail/hooks/use-create-album';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CollectionCard } from '../tabs/dashboard/components/collection-card';
import { MemoryCard } from '../tabs/dashboard/components/memory-card';
import { NewAlbumCard } from '../tabs/dashboard/components/new-album-card';
import { applyPendingOps } from './apply-pipeline';
import styles from './move-memories.module.scss';
import { opId, TransferAction } from './types';

type MoveMemoriesProps = {
  vaultId: number;
};

// One album's memories, as listed on this screen.
type Section = {
  album: Album;
  title: string;
  memories: AlbumContentSerialized[];
};

// A memory card on its way to another album.
type Flight = {
  content: AlbumContentSerialized;
  from: DOMRect;
  dx: number;
  dy: number;
  delay: number;
};

// How long a card takes to reach its album, and the gap between cards.
const FLIGHT_MS = 700;
const FLIGHT_STAGGER_MS = 60;
// More cards than this would only add to the wait.
const MAX_FLIGHTS = 12;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

const ScrollArrows = ({
  scroll,
  label,
}: {
  scroll: ReturnType<typeof useHorizontalScroll<HTMLDivElement>>;
  label: string;
}) =>
  scroll.canScrollLeft || scroll.canScrollRight ? (
    <div className={styles.scrollButtons}>
      <button
        type="button"
        className={styles.scrollButton}
        onClick={() => scroll.scrollPage(-1)}
        disabled={!scroll.canScrollLeft}
        aria-label={`Scroll ${label} left`}
      >
        <ChevronIcon className={styles.scrollLeftIcon} />
      </button>
      <button
        type="button"
        className={styles.scrollButton}
        onClick={() => scroll.scrollPage(1)}
        disabled={!scroll.canScrollRight}
        aria-label={`Scroll ${label} right`}
      >
        <ChevronIcon className={styles.scrollRightIcon} />
      </button>
    </div>
  ) : null;

// An album's memories on one line that scrolls sideways, each a dashboard
// memory card that a click selects.
const AlbumRow = ({
  section,
  selected,
  flying,
  landed,
  pulsing,
  onToggle,
  onSelectAll,
}: {
  section: Section;
  selected: Map<number, number>;
  // Cards on their way out (hidden here), and cards that just arrived.
  flying: Set<number>;
  landed: Set<number>;
  // Just received memories: the row lights up.
  pulsing: boolean;
  onToggle: (memory: AlbumContentSerialized, albumId: number) => void;
  onSelectAll: (all: boolean) => void;
}) => {
  const scroll = useHorizontalScroll<HTMLDivElement>();
  const { album, title, memories } = section;
  const selectedHere = memories.filter((m) => selected.has(m.id)).length;
  const allSelected = memories.length > 0 && selectedHere === memories.length;

  return (
    <section
      className={styles.album}
      aria-label={title}
      data-album-row={album.id}
    >
      <div className={styles.albumHead}>
        <h2 className={styles.albumTitle}>{title}</h2>
        <span className={styles.albumMeta}>
          {plural(memories.length, 'memory', 'memories')}
          {selectedHere > 0 && ` · ${selectedHere} selected`}
        </span>
        <span className={styles.spacer} />
        <button
          type="button"
          className={styles.textButton}
          onClick={() => onSelectAll(!allSelected)}
          disabled={memories.length === 0}
        >
          {allSelected ? 'Unselect all' : 'Select all'}
        </button>
        <ScrollArrows scroll={scroll} label={title} />
      </div>
      <div
        className={classnames(styles.rowWrap, {
          [styles.rowPulse]: pulsing,
        })}
        data-album-landing={album.id}
      >
        <div className={styles.row} ref={scroll.ref}>
          {memories.length === 0 && (
            <p className={styles.emptyRow}>
              No memories yet. Move some here from another album.
            </p>
          )}
          {memories.map((memory) => {
            const isSelected = selected.has(memory.id);
            return (
              <div
                key={memory.id}
                className={classnames(styles.memory, {
                  [styles.memorySelected]: isSelected,
                  [styles.memoryFlying]: flying.has(memory.id),
                  [styles.memoryLanded]: landed.has(memory.id),
                })}
                data-memory-id={memory.id}
              >
                {/* A click selects the memory (or unselects it). */}
                <MemoryCard
                  content={memory}
                  onClick={() => onToggle(memory, album.id)}
                />
                {isSelected && (
                  <span className={styles.check} aria-hidden>
                    <CheckIcon />
                  </span>
                )}
              </div>
            );
          })}
        </div>
        {scroll.canScrollRight && <div className={styles.fade} />}
      </div>
    </section>
  );
};

// "Move Memories" (its own page): tick memories in any album,
// then move or copy them into another album from the bar at the bottom.
export const MoveMemories = ({ vaultId }: MoveMemoriesProps) => {
  const isTouch = useMediaQuery('(hover: none)');
  const createAlbum = useCreateAlbum();

  const [query, setQuery] = useState('');
  // Selected memory id → the album it's in now.
  const [selected, setSelected] = useState<Map<number, number>>(new Map());
  const [picker, setPicker] = useState<TransferAction | null>(null);
  const [newAlbumName, setNewAlbumName] = useState<string | null>(null);
  // The album picked in the album list; nothing happens until it's confirmed.
  const [targetAlbumId, setTargetAlbumId] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ text: string; error: boolean }>();
  const [flights, setFlights] = useState<Flight[]>([]);
  const [flightsAway, setFlightsAway] = useState(false);
  const [flying, setFlying] = useState<Set<number>>(new Set());
  const [landed, setLanded] = useState<Set<number>>(new Set());
  const [pulseAlbumId, setPulseAlbumId] = useState<number | null>(null);
  const pickerScroll = useHorizontalScroll<HTMLDivElement>();
  const screenRef = useRef<HTMLDivElement>(null);

  // Every album with its memories, fetched on arrival and after each change.
  const [allAlbums, setAllAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const reload = async () => {
    try {
      const vault = await VaultsController.getVault(vaultId, true);
      setAllAlbums([
        ...(vault.albums ?? []),
        ...(vault.unansweredAlbums ?? []),
      ]);
      setLoadError(false);
    } catch {
      // Keep what's on screen; with nothing yet, say so.
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, [vaultId]);
  // Where memories can go: every album the user made.
  const targets = useMemo(() => excludeDefaultAlbum(allAlbums), [allAlbums]);

  // Memories not in any album first, then each album.
  const sections: Section[] = useMemo(() => {
    const unsorted = allAlbums.find((album) => album.isDefault);
    const list: Section[] = targets.map((album) => ({
      album,
      title: album.name || 'Untitled album',
      memories: memoriesOf(album),
    }));
    // "Without Album" only shows when something is in it.
    if (unsorted && memoriesOf(unsorted).length > 0) {
      list.unshift({
        album: unsorted,
        title: 'Without Album',
        memories: memoriesOf(unsorted),
      });
    }
    return list;
  }, [allAlbums, targets]);

  // A search matches a memory's name, or an album's name (all its memories).
  const visibleSections = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sections;
    return (
      sections
        .map((section) => {
          const albumMatches = section.title.toLowerCase().includes(q);
          return {
            ...section,
            albumMatches,
            memories: albumMatches
              ? section.memories
              : section.memories.filter((m) =>
                  (m.name || '').toLowerCase().includes(q),
                ),
          };
        })
        // An empty album still shows when its name matches.
        .filter(
          (section) => section.albumMatches || section.memories.length > 0,
        )
    );
  }, [sections, query]);

  const contentsById = useMemo(() => {
    const map = new Map<number, AlbumContentSerialized>();
    sections.forEach((section) =>
      section.memories.forEach((memory) => map.set(memory.id, memory)),
    );
    return map;
  }, [sections]);

  // Memories gone after a refresh can't stay selected.
  useEffect(() => {
    setSelected((current) => {
      const next = new Map(
        Array.from(current).filter(([id]) => contentsById.has(id)),
      );
      return next.size === current.size ? current : next;
    });
  }, [contentsById]);

  useEffect(() => {
    if (!picker) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) setPicker(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [picker, busy]);

  useEffect(() => {
    if (!result) return undefined;
    const timer = setTimeout(() => setResult(undefined), 6000);
    return () => clearTimeout(timer);
  }, [result]);

  const toggle = (memory: AlbumContentSerialized, albumId: number) =>
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(memory.id)) next.delete(memory.id);
      else next.set(memory.id, albumId);
      return next;
    });

  const selectAll = (section: Section, all: boolean) =>
    setSelected((current) => {
      const next = new Map(current);
      section.memories.forEach((memory) => {
        if (all) next.set(memory.id, section.album.id);
        else next.delete(memory.id);
      });
      return next;
    });

  const count = selected.size;
  // An album that already holds every selected memory isn't a destination.
  const holdsAll = (album: Album) =>
    count > 0 && Array.from(selected.values()).every((id) => id === album.id);

  // Cards fly from where they are to the start of the album's row.
  const launch = (ids: number[], targetId: number) => {
    const root = screenRef.current;
    const landing = root?.querySelector<HTMLElement>(
      `[data-album-landing="${targetId}"]`,
    );
    if (!root || !landing || prefersReducedMotion()) return;
    const to = landing.getBoundingClientRect();
    const next: Flight[] = [];
    ids.slice(0, MAX_FLIGHTS).forEach((id, index) => {
      const el = root.querySelector<HTMLElement>(`[data-memory-id="${id}"]`);
      const content = contentsById.get(id);
      if (!el || !content) return;
      const from = el.getBoundingClientRect();
      next.push({
        content,
        from,
        dx: to.left + 16 + Math.min(index, 5) * 18 - from.left,
        dy: to.top + 16 - from.top,
        delay: index * FLIGHT_STAGGER_MS,
      });
    });
    setFlightsAway(false);
    setFlights(next);
    // Next frame: the cards are on screen where they started; now send them.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => setFlightsAway(true)),
    );
  };

  const run = async (
    action: TransferAction,
    target: Pick<Album, 'id' | 'name'>,
  ) => {
    const items = Array.from(selected).filter(
      ([, sourceAlbumId]) => sourceAlbumId !== target.id,
    );
    if (items.length === 0) return;
    const ids = items.map(([id]) => id);
    const verb = action === 'move' ? 'Moving' : 'Copying';
    setBusy(`${verb} ${plural(items.length, 'memory', 'memories')}…`);
    setPicker(null);
    setNewAlbumName(null);

    // Bring the album into view, then send the cards on their way.
    screenRef.current
      ?.querySelector(`[data-album-row="${target.id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    await sleep(prefersReducedMotion() ? 0 : 350);
    launch(ids, target.id);
    if (action === 'move') setFlying(new Set(ids));

    const work = async (): Promise<number[]> => {
      if (action === 'move') {
        // A move keeps the memory itself (its date, file and id) and only
        // changes its album.
        const done: number[] = [];
        for (let i = 0; i < ids.length; i += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await VaultsController.updateContentMetadata(ids[i], {
              albumId: target.id,
            });
            done.push(ids[i]);
          } catch {
            // Counted below: it stays where it was, still selected.
          }
        }
        return done;
      }
      const copied = await applyPendingOps({
        vaultId,
        contentsById,
        ops: items.map(([contentId, sourceAlbumId]) => ({
          id: opId(contentId, target.id),
          contentId,
          sourceAlbumId,
          targetAlbumId: target.id,
          action: 'copy' as const,
        })),
      });
      const failedIds = new Set(copied.failedOps.map((op) => op.contentId));
      return ids.filter((id) => !failedIds.has(id));
    };

    const [doneIds] = await Promise.all([
      work(),
      sleep(
        prefersReducedMotion()
          ? 0
          : FLIGHT_MS + Math.min(ids.length, MAX_FLIGHTS) * FLIGHT_STAGGER_MS,
      ),
    ]);

    // Moved cards land in the album's row right away; the fresh copy of the
    // albums replaces this guess a moment later.
    if (action === 'move' && doneIds.length > 0) {
      const moved = new Set(doneIds);
      setAllAlbums(
        allAlbums.map((album) => {
          const contents = (album.contents ?? []).filter(
            (content) => !moved.has(content.id),
          );
          if (album.id === target.id) {
            doneIds.forEach((id) => {
              const content = contentsById.get(id);
              if (content) contents.unshift(content);
            });
          }
          return { ...album, contents };
        }),
      );
      setLanded(new Set(doneIds));
    }
    setFlights([]);
    setFlying(new Set());
    setPulseAlbumId(target.id);

    const ok = doneIds.length;
    const failed = ids.length - ok;
    const doneWord = action === 'move' ? 'Moved' : 'Copied';
    const name = target.name || 'the album';
    setResult(
      failed > 0
        ? {
            text: `${doneWord} ${ok} of ${ids.length} to ${name}. ${plural(failed, 'memory', 'memories')} couldn’t be ${action === 'move' ? 'moved' : 'copied'}; they’re still selected.`,
            error: true,
          }
        : {
            text: `${doneWord} ${plural(ok, 'memory', 'memories')} to ${name}.`,
            error: false,
          },
    );
    // What failed stays selected, so it can be tried again.
    setSelected((current) => {
      const next = new Map(current);
      doneIds.forEach((id) => next.delete(id));
      return next;
    });
    setBusy(null);
    await reload();
  };

  // The landing glow fades after a moment.
  useEffect(() => {
    if (landed.size === 0 && pulseAlbumId === null) return undefined;
    const timer = setTimeout(() => {
      setLanded(new Set());
      setPulseAlbumId(null);
    }, 1800);
    return () => clearTimeout(timer);
  }, [landed, pulseAlbumId]);

  const createAndRun = async () => {
    const name = newAlbumName?.trim();
    if (!picker || !name) return;
    setBusy('Creating the album…');
    const album = await createAlbum(vaultId, name);
    if (!album) {
      setBusy(null);
      return;
    }
    // Its row has to be on screen for the cards to fly to it.
    await reload();
    await run(picker, album);
  };

  const pickerVerb = picker === 'copy' ? 'Copy' : 'Move';
  const targetAlbum = targets.find((album) => album.id === targetAlbumId);

  const openPicker = (action: TransferAction) => {
    setTargetAlbumId(null);
    setNewAlbumName(null);
    setPicker(action);
  };

  return (
    <div ref={screenRef} className={styles.screen}>
      <header className={styles.header}>
        <h1 className={styles.title}>Move Memories</h1>
        <p className={styles.subtitle}>
          {isTouch ? 'Tap' : 'Click'} the memories you want to move. A bar
          appears at the bottom with what you can do.
        </p>
      </header>

      <main className={styles.body}>
        <label className={styles.search} htmlFor="move-memories-search">
          <SearchIcon className={styles.searchIcon} />
          <input
            id="move-memories-search"
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search memories or albums"
            aria-label="Search memories or albums"
          />
        </label>

        {loading && (
          <div className={styles.loading}>
            <Spinner />
          </div>
        )}

        {!loading && loadError && allAlbums.length === 0 && (
          <p className={styles.empty}>
            Your albums couldn’t be loaded. Refresh the page to try again.
          </p>
        )}

        {!loading && !loadError && visibleSections.length === 0 && (
          <p className={styles.empty}>
            {query.trim()
              ? `No memories match “${query.trim()}”.`
              : 'There are no albums in this vault yet.'}
          </p>
        )}

        {visibleSections.map((section) => (
          <AlbumRow
            key={section.album.id}
            section={section}
            selected={selected}
            flying={flying}
            landed={landed}
            pulsing={pulseAlbumId === section.album.id}
            onToggle={toggle}
            onSelectAll={(all) => selectAll(section, all)}
          />
        ))}
      </main>

      {picker && (
        <div
          className={styles.pickerBackdrop}
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setPicker(null);
          }}
        >
          <section
            className={styles.picker}
            aria-labelledby="move-memories-picker-title"
          >
            <div className={styles.pickerHead}>
              <div className={styles.headerText}>
                <h2
                  id="move-memories-picker-title"
                  className={styles.pickerTitle}
                >
                  {pickerVerb} {plural(count, 'memory', 'memories')} to…
                </h2>
                <p className={styles.subtitle}>
                  {picker === 'copy'
                    ? 'Each memory stays where it is, and a copy goes into the album you pick.'
                    : 'Pick the album they should be in. They leave the album they’re in now.'}
                </p>
              </div>
              <ScrollArrows scroll={pickerScroll} label="albums" />
              <button
                type="button"
                className={styles.close}
                onClick={() => setPicker(null)}
                aria-label="Close album list"
                disabled={!!busy}
              >
                <CrossIcon />
              </button>
            </div>

            {newAlbumName !== null ? (
              <form
                className={styles.newAlbum}
                onSubmit={(e) => {
                  e.preventDefault();
                  createAndRun();
                }}
              >
                <label
                  className={styles.newAlbumLabel}
                  htmlFor="move-memories-new-album"
                >
                  New album name
                  <input
                    id="move-memories-new-album"
                    value={newAlbumName}
                    onChange={(e) => setNewAlbumName(e.target.value)}
                    maxLength={100}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                </label>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => setNewAlbumName(null)}
                  disabled={!!busy}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className={styles.primary}
                  disabled={!newAlbumName.trim() || !!busy}
                >
                  Create and {pickerVerb.toLowerCase()}
                </button>
              </form>
            ) : (
              <div className={styles.rowWrap}>
                <div
                  className={classnames(styles.row, styles.albumsRow)}
                  ref={pickerScroll.ref}
                >
                  <NewAlbumCard onClick={() => setNewAlbumName('')} />
                  {targets.map((album) => {
                    const disabled = holdsAll(album) || !!busy;
                    const isTarget = album.id === targetAlbumId;
                    return (
                      <div
                        key={album.id}
                        className={classnames(styles.pick, {
                          [styles.pickSelected]: isTarget,
                        })}
                      >
                        <CollectionCard
                          album={album}
                          onClick={
                            disabled
                              ? undefined
                              : () =>
                                  setTargetAlbumId(isTarget ? null : album.id)
                          }
                          isDropDisabled={disabled}
                        />
                        {isTarget && (
                          <span className={styles.check} aria-hidden>
                            <CheckIcon />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {pickerScroll.canScrollRight && <div className={styles.fade} />}
              </div>
            )}
            {busy && (
              <p className={styles.busy} role="status">
                {busy}
              </p>
            )}
            {newAlbumName === null && (
              <div className={styles.pickerFooter}>
                <p className={styles.pickerSummary}>
                  {targetAlbum ? (
                    <>
                      {pickerVerb} {plural(count, 'memory', 'memories')} to{' '}
                      <strong>{targetAlbum.name}</strong>
                    </>
                  ) : (
                    'Click the album they should go to.'
                  )}
                </p>
                <button
                  type="button"
                  className={styles.secondary}
                  onClick={() => setPicker(null)}
                  disabled={!!busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.primary}
                  onClick={() => targetAlbum && run(picker, targetAlbum)}
                  disabled={!targetAlbum || !!busy}
                >
                  {pickerVerb} {plural(count, 'memory', 'memories')}
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {count > 0 && !picker && (
        <div className={styles.actionBar}>
          <span className={styles.actionCount}>{count} selected</span>
          <button
            type="button"
            className={styles.actionPrimary}
            onClick={() => openPicker('move')}
          >
            Move to album
          </button>
          <button
            type="button"
            className={styles.actionSecondary}
            onClick={() => openPicker('copy')}
          >
            Copy to album
          </button>
          <button
            type="button"
            className={styles.actionClear}
            onClick={() => setSelected(new Map())}
          >
            Clear
          </button>
        </div>
      )}

      {result && !picker && (
        <div
          className={classnames(styles.result, {
            [styles.resultError]: result.error,
            // Above the action bar while memories are still selected.
            [styles.resultRaised]: count > 0,
          })}
          role="status"
        >
          {result.text}
        </div>
      )}

      {/* Copies of the cards in flight; the real ones wait out of sight. */}
      {flights.length > 0 && (
        <div className={styles.flightLayer} aria-hidden inert>
          {flights.map((flight) => (
            <div
              key={flight.content.id}
              className={styles.flight}
              style={{
                left: flight.from.left,
                top: flight.from.top,
                width: flight.from.width,
                transform: flightsAway
                  ? `translateX(${flight.dx}px)`
                  : 'translateX(0)',
                transitionDelay: `${flight.delay}ms`,
              }}
            >
              <div
                className={styles.flightCard}
                style={{
                  transform: flightsAway
                    ? `translateY(${flight.dy}px) scale(0.42) rotate(-8deg)`
                    : 'translateY(0)',
                  transitionDelay: `${flight.delay}ms`,
                }}
              >
                <MemoryCard content={flight.content} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
